import React, { createContext, useContext, useState } from 'react'

// TabContext
const TabContext = createContext()

export const useTabContext = () => {
  const context = useContext(TabContext)
  if (!context) {
    throw new Error('useTabContext must be used within a TabContext.Provider')
  }
  return context
}

export const TabContextProvider = ({ children, value, onChange }) => {
  return (
    <TabContext.Provider value={{ value, onChange }}>
      {children}
    </TabContext.Provider>
  )
}

// TabList
const TabList = React.forwardRef(
  (
    {
      children,
      orientation = 'horizontal',
      variant = 'standard',
      scrollButtons = false,
      onChange,
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    const getTabListStyles = () => {
      const styles = {
        display: 'flex',
        borderBottom: orientation === 'horizontal' ? '1px solid' : 'none',
        borderRight: orientation === 'vertical' ? '1px solid' : 'none',
        borderColor: 'currentColor',
        minHeight: '48px'
      }

      if (orientation === 'vertical') {
        styles.flexDirection = 'column'
        styles.borderRight = '1px solid'
        styles.borderRightColor = 'currentColor'
      }

      return styles
    }

    const tabListStyles = getTabListStyles()

    return (
      <div
        ref={ref}
        className={className}
        style={{ ...tabListStyles, ...sx }}
        role='tablist'
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child)
          }
          return child
        })}
      </div>
    )
  }
)

TabList.displayName = 'TabList'

// Tab
const Tab = React.forwardRef(
  (
    {
      children,
      value,
      label,
      disabled = false,
      disableRipple = false,
      icon,
      iconPosition = 'start',
      onChange: propOnChange, // Rename to avoid confusion
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    // Filter out custom props that shouldn't be passed to DOM
    const { disableRipple: _, ...domProps } = props

    // Get current active tab from context
    const { value: contextValue, onChange: contextOnChange } = useTabContext()
    const isSelected = contextValue === value

    // Use context onChange if available, otherwise fall back to prop
    const handleChange = contextOnChange || propOnChange

    const handleClick = (event) => {
      if (!disabled && handleChange) {
        handleChange(event, value)
      }
    }

    const getTabStyles = () => {
      const styles = {
        padding: '12px 16px',
        minHeight: '48px',
        minWidth: '90px',
        textTransform: 'none',
        fontWeight: isSelected ? '600' : '500',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        border: 'none',
        background: 'none',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
        opacity: disabled ? 0.38 : 1,
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        outline: 'none',
        position: 'relative'
      }

      // Add active indicator styles
      if (isSelected) {
        styles.borderBottom = '2px solid currentColor'
        styles.color = 'primary'
      }

      return styles
    }

    const tabStyles = getTabStyles()

    const renderContent = () => {
      if (icon && iconPosition === 'start') {
        return (
          <React.Fragment>
            {icon}
            {label || children}
          </React.Fragment>
        )
      } else if (icon && iconPosition === 'end') {
        return (
          <React.Fragment>
            {label || children}
            {icon}
          </React.Fragment>
        )
      }
      return label || children
    }

    return (
      <button
        ref={ref}
        className={className}
        style={{ ...tabStyles, ...sx }}
        onClick={handleClick}
        disabled={disabled}
        role='tab'
        aria-selected={isSelected}
        data-selected={isSelected}
        data-value={value}
        {...domProps}
      >
        {renderContent()}
      </button>
    )
  }
)

Tab.displayName = 'Tab'

// TabPanel
const TabPanel = React.forwardRef(
  ({ children, value, index, className = '', sx = {}, ...props }, ref) => {
    const { value: contextValue } = useTabContext()
    const isSelected = contextValue === value

    if (!isSelected) {
      return null
    }

    const getTabPanelStyles = () => {
      return {
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }
    }

    const tabPanelStyles = getTabPanelStyles()

    return (
      <div
        ref={ref}
        className={className}
        style={{ ...tabPanelStyles, ...sx }}
        role='tabpanel'
        hidden={!isSelected}
        {...props}
      >
        {children}
      </div>
    )
  }
)

TabPanel.displayName = 'TabPanel'

export { TabContext, TabList, Tab, TabPanel }
